import type { Recommendation, RecCategory, RecDifficulty } from '@lensora/shared'
import { cn } from '@lensora/ui'
import { Check, X } from 'lucide-react'

// ── Category + difficulty presentation ──────────────────────────────────────────

const CATEGORY_LABELS: Record<RecCategory, string> = {
  content: 'content',
  technical: 'technical',
  authority: 'authority',
  structured_data: 'structured data',
  entity: 'entity',
}

const DIFFICULTY_LABELS: Record<RecDifficulty, string> = {
  easy: 'easy',
  medium: 'medium',
  hard: 'hard',
}

const DIFFICULTY_COLORS: Record<RecDifficulty, string> = {
  easy: 'border-feedback-success/30 bg-feedback-success/10 text-feedback-success',
  medium: 'border-feedback-warning/30 bg-feedback-warning/10 text-feedback-warning',
  hard: 'border-feedback-error/30 bg-feedback-error/10 text-feedback-error',
}

// estimatedImpact is generated on a 0-10 scale (see generateRecommendations in
// apps/api/src/lib/crawl/recommendations.ts) — normalize to 5 bars for display.
function ImpactBars({ impact }: { impact: number }) {
  const filledBars = Math.round((Math.max(0, Math.min(10, impact)) / 10) * 5)
  return (
    <div className="flex items-center gap-0.5" aria-label={`Impact ${impact} of 10`}>
      {Array.from({ length: 5 }, (_, i) => (
        <div
          key={i}
          className={cn('h-3 w-1 rounded-full', i < filledBars ? 'bg-brand-accent' : 'bg-surface-overlay')}
        />
      ))}
    </div>
  )
}

interface ImprovementActionCardProps {
  recommendation: Recommendation
  onComplete: (id: string) => void
  onDismiss: (id: string) => void
  isUpdating: boolean
}

export function ImprovementActionCard({ recommendation, onComplete, onDismiss, isUpdating }: ImprovementActionCardProps) {
  const isDone = recommendation.status === 'completed'
  const isDismissed = recommendation.status === 'dismissed'

  return (
    <div
      className={cn(
        'flex flex-col gap-3 rounded-lg border border-border bg-surface p-5',
        isDismissed && 'opacity-50'
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <h3 className="text-sm font-semibold leading-snug text-text">{recommendation.title}</h3>
        <ImpactBars impact={recommendation.estimatedImpact} />
      </div>

      <p className="text-sm leading-relaxed text-text-muted">{recommendation.description}</p>

      {recommendation.implementationSteps.length > 0 && (
        <ol className="space-y-1 text-xs text-text-muted">
          {recommendation.implementationSteps.map((step, i) => (
            <li key={i} className="flex gap-2">
              <span className="shrink-0 font-medium text-text">{i + 1}.</span>
              <span>{step}</span>
            </li>
          ))}
        </ol>
      )}

      <div className="mt-1 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="rounded-full border border-border bg-surface-raised px-2.5 py-0.5 text-[11px] text-text-muted">
            {CATEGORY_LABELS[recommendation.category]}
          </span>
          <span className={cn('rounded-full border px-2.5 py-0.5 text-[11px] font-medium', DIFFICULTY_COLORS[recommendation.difficulty])}>
            {DIFFICULTY_LABELS[recommendation.difficulty]}
          </span>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <button
            onClick={() => onComplete(recommendation.id)}
            disabled={isUpdating}
            className={cn(
              'flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-colors disabled:opacity-50',
              isDone
                ? 'border-feedback-success/30 bg-feedback-success/10 text-feedback-success'
                : 'border-border text-text-muted hover:border-feedback-success/30 hover:bg-feedback-success/10 hover:text-feedback-success'
            )}
          >
            <Check className="h-3 w-3" />
            {isDone ? 'Done' : 'Mark done'}
          </button>
          <button
            onClick={() => onDismiss(recommendation.id)}
            disabled={isUpdating}
            className="rounded-full border border-border p-1 text-text-muted transition-colors hover:border-feedback-error/30 hover:bg-feedback-error/10 hover:text-feedback-error disabled:opacity-50"
            aria-label="Dismiss"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      </div>
    </div>
  )
}
